import { CommonModule } from '@angular/common';
import { Component, inject } from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MatIcon } from '@angular/material/icon';
import { ActivatedRoute, Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { NgxSpinnerService } from 'ngx-spinner';
import { StaffService } from '../../../../services/staff.service';
import { FNFResponse } from '../../../../models/response';
import { formatUsPhone, usPhoneDigits } from '../../../../utils/us-phone.util';
import { countryOf, flagOf } from '../../../../utils/phone-number.util';

@Component({
  selector: 'app-staff-form',
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule, MatIcon],
  templateUrl: './staff-form.component.html',
  styleUrl: './staff-form.component.css',
})
export class StaffFormComponent {
  private fb = inject(FormBuilder);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private toastr = inject(ToastrService);
  private spinner = inject(NgxSpinnerService);
  private staffService = inject(StaffService);

  /** Set on the edit route; null when adding. */
  staff: any = null;
  allStaffs: any[] = [];
  loading = true;
  isDuplicate = false;

  // Unchanged from the dialog this replaces: same roles, shifts, validation
  // and payload. Only the presentation is new.
  readonly roles: any[] = [
    { name: 'restaurant_admin', displayName: 'Restaurant Admin', id: 1 },
    { name: 'restaurant_manager', displayName: 'Restaurant Manager', id: 2 },
    { name: 'restaurant_waiter', displayName: 'Restaurant Waiter', id: 3 },
  ];

  readonly shifts: any[] = [
    { name: 'full_time', displayName: 'Full Time', hint: 'Regular fixed schedule' },
    { name: 'part_time', displayName: 'Part Time', hint: 'Reduced hours schedule' },
    { name: 'shift_based', displayName: 'Shift-Based', hint: 'Rotating or variable shifts' },
  ];

  staffForm: FormGroup = this.buildForm(null);

  ngOnInit() {
    this.route.paramMap.subscribe((params) => this.load(params.get('id')));
  }

  private load(userId: string | null) {
    this.loading = true;
    this.staffService.getAllStaffs().subscribe({
      next: (response: FNFResponse<any>) => {
        this.allStaffs = response?.data || [];
        this.staff = userId
          ? this.allStaffs.find((s: any) => String(s.user_id) === String(userId)) || null
          : null;
        // An edit link for an unknown or removed id must not fall back to the
        // Add form: saving it would invite a new person (FNF-1532). Same as Set PIN.
        if (userId && !this.staff) {
          this.toastr.error('That staff member could not be found.', 'Error');
          this.cancel();
          return;
        }
        this.staffForm = this.buildForm(this.staff);
        this.loading = false;
      },
      error: () => {
        this.toastr.error('Unable to load the staff member. Please try again later.', 'Error');
        this.loading = false;
        this.cancel();
      },
    });
  }

  private buildForm(staff: any): FormGroup {
    const name = [Validators.required, Validators.minLength(3), Validators.maxLength(50)];
    return this.fb.group({
      first_name: [staff?.first_name ?? '', name],
      last_name: [staff?.last_name ?? '', name],
      email: [
        // The email identifies the account, so it is fixed once invited.
        { value: staff?.email ?? '', disabled: !!staff },
        [Validators.required, Validators.pattern(/^\w+([-+.']\w+)*@\w+([-.]\w+)*\.\w+([-.]\w+)*$/)],
      ],
      display_name: [staff?.display_name ?? '', name],
      phone_number: [
        this.formatPhoneNumber(staff?.phone_number ?? ''),
        [Validators.required, Validators.pattern(/^\(\d{3}\) \d{3}-\d{4}$/)],
      ],
      user_role: [staff?.user_role ?? '', Validators.required],
      shift_type: [staff?.shift_type ?? 'shift_based', Validators.required],
    });
  }

  /** Staff numbers are US: the field shows 🇺🇸 +1 and takes the 10 national digits. */
  readonly phoneCountry = countryOf('US');
  readonly phoneFlag = flagOf(this.phoneCountry);

  /** Stored "+14155550100" or "4155550100" both read back as "(415) 555-0100". */
  formatPhoneNumber(value: string): string {
    return formatUsPhone(value);
  }

  onPhoneInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    const formatted = this.formatPhoneNumber(input.value);
    input.value = formatted;
    this.staffForm.get('phone_number')?.setValue(formatted, { emitEvent: false });
  }

  filterEmail() {
    const email = (this.staffForm.get('email')?.value || '').trim().toLowerCase();
    this.isDuplicate = this.allStaffs.some(
      (s: any) => s.email?.trim().toLowerCase() === email && s.email !== this.staff?.email,
    );
  }

  selectShift(shift: any) {
    this.staffForm.get('shift_type')?.setValue(shift.name);
  }

  isShift(name: string): boolean {
    return this.staffForm.get('shift_type')?.value === name;
  }

  cancel() {
    this.router.navigate(['/main/staffs']);
  }

  save() {
    if (this.staffForm.invalid || this.isDuplicate) {
      return;
    }

    const payload: any = { ...this.staffForm.getRawValue() };
    // E.164 with the locked US code: "(806) 999-5417" -> "+18069995417".
    payload.phone_number = `+${this.phoneCountry.dialCode}${usPhoneDigits(payload.phone_number)}`;

    const raw = localStorage.getItem('user');
    if (raw) {
      try {
        payload.restaurant_id = JSON.parse(raw).restaurant_id;
      } catch {
        /* stored value is unusable — the API rejects the missing id */
      }
    }

    this.spinner.show();
    const request = this.staff
      ? this.staffService.editStaff(payload, this.staff.user_id)
      : this.staffService.createNewStaff(payload);

    request.subscribe({
      next: (response: FNFResponse<any>) => {
        this.spinner.hide();
        if (!response.data) {
          const message = response.error?.error?.message || response.error?.message;
          this.toastr.error(message || 'No data found!', 'Error');
          return;
        }
        this.toastr.success(
          this.staff ? 'Staff updated successfully' : 'Staff added successfully',
          'Success',
        );
        this.cancel();
      },
      error: () => {
        this.spinner.hide();
        this.toastr.error('Unable to complete your action. Please try again later.', 'Error');
      },
    });
  }
}
